import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSetup } from '../../../core/contexts/SetupContext';
import { secureGetItem, secureSetItem } from '../../utils/secureStorage';
import {
  User, Edit, Lock, CheckCircle, AlertCircle, Shield, LogOut, Mail, Phone,
  Building2, Briefcase, Bell, Globe, Calendar, Camera, IdCard, History,
} from 'lucide-react';
import settingsService from '../../../core/services/settingsService';
import { updateAdminProfile, changePasswordWithCurrent } from '../../../core/services/passwordResetService';
import { API_BASE } from '../../../shared/utils/apiConfig';
import localApiAdapter from '../../../core/services/localApiAdapter';
import {
  DEFAULT_NOTIFICATION_PREFS,
  DEFAULT_PROFILE_PREFS,
  formatProfileDate,
  getProfilePhonePlaceholder,
  loadProfilePreferences,
  normalizeProfilePhone,
  resolveProfilePhone,
  saveProfilePreferences,
  syncLocalUserProfile,
  validateProfilePassword,
  validateProfilePhone,
} from '../../utils/profileHelpers';
import { SAUDI_CURRENCY_CODE } from '../../utils/brandCurrency';

const cardStyle = {
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '1.5rem',
  padding: '1.5rem',
  marginBottom: '1.5rem',
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '0.75rem',
  color: 'white',
  fontSize: '0.95rem',
  outline: 'none',
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: '0.5rem',
};

function readStoredUser() {
  try {
    let storedUser = secureGetItem('qawaem_user');
    if (!storedUser) {
      const raw = localStorage.getItem('qawaem_user');
      if (raw) {
        try { storedUser = JSON.parse(raw); } catch { /* ignore */ }
      }
    }
    return storedUser || null;
  } catch {
    return null;
  }
}

const Profile = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.shared.profile.${key}`, opts);
  const navigate = useNavigate();
  const { logout } = useSetup();

  const [user, setUser] = useState(null);
  const [hrEmployee, setHrEmployee] = useState(null);
  const [prefs, setPrefs] = useState(() => loadProfilePreferences());
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    profilePhoto: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    securityQuestion: '',
    securityAnswer: '',
    twoFactorEnabled: false,
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const showHijri = prefs.showHijri;
  const fmtDate = useCallback(
    (value) => formatProfileDate(value, { showHijri, language: i18n.language }),
    [showHijri, i18n.language],
  );

  const loginHistory = useMemo(() => {
    try {
      const raw = localStorage.getItem('qawaem_login_history');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    const stored = readStoredUser();
    if (stored?.loginTime) {
      return [{ at: stored.loginTime, device: t('modules.shared.profile.current_device') }];
    }
    return [];
  }, [user?.lastLogin, t]);

  const loadProfile = useCallback(async () => {
    try {
      const storedUser = readStoredUser();
      let settingsBlob = null;
      try {
        settingsBlob = await settingsService.get();
      } catch (err) {
        console.warn('Could not load settings for profile:', err?.message || err);
      }

      const settingsAdmin = settingsBlob?.adminUser || null;
      const resolvedPhone = resolveProfilePhone({ storedUser, settingsBlob });

      const profile = {
        name: storedUser?.username || settingsAdmin?.username || t('modules.shared.profile.default_user'),
        nameAr: storedUser?.nameAr || settingsAdmin?.nameAr || '',
        email: storedUser?.email || settingsAdmin?.email || '',
        phone: resolvedPhone,
        jobTitle: storedUser?.jobTitle || settingsAdmin?.jobTitle || '',
        department: storedUser?.department || settingsAdmin?.department || '',
        profilePhoto: storedUser?.profilePhoto || '',
        role: storedUser?.role || t('modules.shared.profile.default_role'),
        lastLogin: storedUser?.loginTime || null,
        updatedAt: storedUser?.updatedAt || settingsAdmin?.updatedAt || null,
        employeeId: storedUser?.employeeId || settingsAdmin?.employeeId || '',
      };
      setUser(profile);

      const loadedPrefs = loadProfilePreferences();
      setPrefs(loadedPrefs);
      setFormData((prev) => ({
        ...prev,
        name: profile.name,
        nameAr: profile.nameAr,
        email: profile.email,
        phone: profile.phone,
        jobTitle: profile.jobTitle,
        department: profile.department,
        profilePhoto: profile.profilePhoto,
        twoFactorEnabled: !!loadedPrefs.twoFactorEnabled,
      }));

      const lookupEmail = profile.email?.trim();
      if (lookupEmail) {
        try {
          const res = await localApiAdapter.get('/api/employee-portal/profile', {
            params: { email: lookupEmail },
          });
          const row = res?.data ?? res;
          if (row?.id) setHrEmployee(row);
        } catch {
          /* HR record optional */
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setUser({
        name: t('modules.shared.profile.default_user'),
        role: t('modules.shared.profile.default_role'),
      });
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePrefChange = (key, value) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      saveProfilePreferences(next);
      return next;
    });
  };

  const handleNotificationChange = (key, checked) => {
    setPrefs((prev) => {
      const next = {
        ...prev,
        notifications: { ...(prev.notifications || DEFAULT_NOTIFICATION_PREFS), [key]: checked },
      };
      saveProfilePreferences(next);
      return next;
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: p('msg_photo_too_large') });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, profilePhoto: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = formData.name?.trim() || '';
    if (!trimmedName) {
      setMessage({ type: 'error', text: p('msg_name_required') });
      return;
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setMessage({ type: 'error', text: p('msg_invalid_email') });
      return;
    }
    if (!validateProfilePhone(formData.phone)) {
      setMessage({ type: 'error', text: p('msg_invalid_phone') });
      return;
    }

    setSaving(true);
    const normalizedPhone = normalizeProfilePhone(formData.phone);

    try {
      const profileResult = await updateAdminProfile({
        currentUsername: user?.name || trimmedName,
        username: trimmedName,
        email: formData.email,
        jobTitle: formData.jobTitle,
        phone: normalizedPhone,
        department: formData.department,
      });

      if (!profileResult.success && API_BASE) {
        setMessage({ type: 'error', text: profileResult.error || p('msg_profile_error') });
        setSaving(false);
        return;
      }

      syncLocalUserProfile(
        {
          username: trimmedName,
          nameAr: formData.nameAr,
          email: formData.email,
          jobTitle: formData.jobTitle,
          phone: normalizedPhone,
          department: formData.department,
          profilePhoto: formData.profilePhoto,
        },
        { secureGetItem, secureSetItem },
      );

      saveProfilePreferences({
        ...prefs,
        language: 'ar',
        twoFactorEnabled: formData.twoFactorEnabled,
      });

      setUser((prev) => ({
        ...prev,
        name: trimmedName,
        nameAr: formData.nameAr,
        email: formData.email,
        phone: normalizedPhone,
        jobTitle: formData.jobTitle,
        department: formData.department,
        profilePhoto: formData.profilePhoto,
        updatedAt: new Date().toISOString(),
      }));

      setMessage({ type: 'success', text: p('msg_profile_updated') });
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Profile update failed:', error);
      setMessage({ type: 'error', text: p('msg_profile_error') });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: p('msg_password_mismatch') });
      return;
    }

    const ruleKey = validateProfilePassword(formData.newPassword);
    if (ruleKey) {
      setMessage({ type: 'error', text: p(`password_rule_${ruleKey}`) });
      return;
    }

    setSaving(true);
    try {
      const username = user?.name || formData.name?.trim() || '';
      const apiResult = await changePasswordWithCurrent({
        username,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (!apiResult.success) {
        setMessage({ type: 'error', text: apiResult.error || p('msg_wrong_current_password') });
        setSaving(false);
        return;
      }

      try {
        const adminJson = localStorage.getItem('qawaem_admin_user');
        if (adminJson) {
          const adminData = JSON.parse(adminJson);
          adminData.password = formData.newPassword;
          localStorage.setItem('qawaem_admin_user', JSON.stringify(adminData));
        }
        const current = await settingsService.get();
        if (current?.adminUser) {
          await settingsService.update({
            ...current,
            hasAdminUser: true,
            adminUser: { ...current.adminUser, password: formData.newPassword },
          });
        }
      } catch (storageErr) {
        console.warn('Local password sync failed:', storageErr);
      }

      setMessage({ type: 'success', text: p('msg_password_updated') });
      setFormData((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setIsChangingPassword(false);
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: p('msg_password_error') });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    try {
      logout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    navigate('/login', { replace: true });
  };

  const isSaudiNational = hrEmployee
    ? String(hrEmployee.nationalite || hrEmployee.nationality || '').toLowerCase().includes('saudi')
      || String(hrEmployee.nationalite || '').includes('سعود')
    : null;

  const avatarSrc = isEditingProfile ? formData.profilePhoto : user?.profilePhoto;
  const avatarLetter = user?.nameAr?.charAt(0) || user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <motion.div
      style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: 'white', margin: 0 }}>{p('title')}</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>{p('subtitle')}</p>
      </div>

      {message.text ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            padding: '1rem 1.25rem',
            borderRadius: '0.75rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: message.type === 'success' ? 'rgb(52, 211, 153)' : 'rgb(248, 113, 113)',
          }}
        >
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </motion.div>
      ) : null}

      {/* Identity card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ position: 'relative' }}>
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-white/20" />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-700 rounded-full flex items-center justify-center text-3xl font-bold text-white">
                {avatarLetter}
              </div>
            )}
            {isEditingProfile ? (
              <label className="absolute bottom-0 right-0 p-1.5 bg-blue-600 rounded-full cursor-pointer">
                <Camera size={14} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              </label>
            ) : null}
          </div>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {user?.nameAr || user?.name || p('default_user')}
            </h2>
            {user?.nameAr && user?.name ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '0.15rem', fontSize: '0.9rem' }}>{user.name}</p>
            ) : null}
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '0.25rem' }}>
              {user?.jobTitle || user?.role || p('default_role')}
              {user?.department ? ` — ${user.department}` : ''}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.75rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
              {user?.lastLogin ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <History size={14} />
                  {p('last_login')}: {fmtDate(user.lastLogin)}
                </span>
              ) : null}
              {user?.updatedAt ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Calendar size={14} />
                  {p('profile_updated')}: {fmtDate(user.updatedAt)}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {!isEditingProfile ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {[
                { icon: User, label: p('name_en'), value: user?.name },
                { icon: User, label: p('name_ar'), value: user?.nameAr },
                { icon: Mail, label: p('email'), value: user?.email },
                { icon: Phone, label: p('phone'), value: user?.phone },
                { icon: Briefcase, label: p('job_title'), value: user?.jobTitle },
                { icon: Building2, label: p('department'), value: user?.department },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label}>
                  <label style={labelStyle}><Icon size={16} />{label}</label>
                  <p style={{ color: 'white', fontSize: '1rem' }}>{value || p('not_defined')}</p>
                </div>
              ))}
            </div>
            <motion.button
              type="button"
              onClick={() => setIsEditingProfile(true)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-semibold"
            >
              <Edit size={16} />
              {p('edit_btn')}
            </motion.button>
          </div>
        ) : (
          <form onSubmit={handleProfileSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label style={labelStyle}><User size={16} />{p('name_en')}</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder={p('enter_name')} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}><User size={16} />{p('name_ar')}</label>
                <input type="text" name="nameAr" value={formData.nameAr} onChange={handleChange} placeholder={p('enter_name_ar')} dir="rtl" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}><Mail size={16} />{p('email')}</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder={p('enter_email')} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}><Phone size={16} />{p('phone')}</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={getProfilePhonePlaceholder()} dir="ltr" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}><Briefcase size={16} />{p('job_title')}</label>
                <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder={p('enter_job_title')} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}><Building2 size={16} />{p('department')}</label>
                <input type="text" name="department" value={formData.department} onChange={handleChange} placeholder={p('enter_department')} style={inputStyle} />
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button type="submit" disabled={saving} whileHover={{ scale: 1.03 }} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white rounded-xl font-semibold disabled:opacity-60">
                <CheckCircle size={16} />{t('common.save')}
              </motion.button>
              <motion.button type="button" onClick={() => setIsEditingProfile(false)} className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold">
                {t('common.cancel')}
              </motion.button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Saudi employee compliance (read-only when linked to HR) */}
      {hrEmployee ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} style={cardStyle}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
            <IdCard size={20} />
            {p('section_saudi_employee')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              isSaudiNational
                ? { label: p('national_id'), value: hrEmployee.numero_identite_national || hrEmployee.cin || '—' }
                : { label: p('iqama_number'), value: hrEmployee.numero_iqama || hrEmployee.iqama || '—' },
              { label: p('gosi_number'), value: hrEmployee.numero_gosi || hrEmployee.securite_sociale || '—' },
              { label: p('employee_id'), value: hrEmployee.matricule || hrEmployee.id || '—' },
              { label: p('hire_date'), value: fmtDate(hrEmployee.dateEmbauche || hrEmployee.date_recrutement) },
              { label: p('nationality_type'), value: isSaudiNational ? p('nationality_saudi') : p('nationality_expat') },
            ].map(({ label, value }) => (
              <div key={label}>
                <label style={labelStyle}>{label}</label>
                <p style={{ color: 'white' }}>{value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      ) : null}

      {/* Localization */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={cardStyle}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
          <Globe size={20} />
          {p('section_localization')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label style={labelStyle}>{p('interface_language')}</label>
            <p style={{ color: 'white' }}>{p('language_arabic')}</p>
          </div>
          <div>
            <label style={labelStyle}>{p('date_format')}</label>
            <p style={{ color: 'white' }}>{DEFAULT_PROFILE_PREFS.dateLocale}{showHijri ? ` + ${p('hijri_calendar')}` : ''}</p>
          </div>
          <div>
            <label style={labelStyle}>{p('number_format')}</label>
            <p style={{ color: 'white' }}>{DEFAULT_PROFILE_PREFS.numberLocale}</p>
          </div>
          <div>
            <label style={labelStyle}>{p('currency')}</label>
            <p style={{ color: 'white' }}>{SAUDI_CURRENCY_CODE} ({p('currency_sar')})</p>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.showHijri}
                onChange={(e) => handlePrefChange('showHijri', e.target.checked)}
                className="rounded"
              />
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>{p('show_hijri_dates')}</span>
            </label>
          </div>
        </div>
      </motion.div>

      {/* Notifications */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} style={cardStyle}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
          <Bell size={20} />
          {p('section_notifications')}
        </h2>
        <div className="space-y-3">
          {[
            { key: 'emailNotifications', label: p('notify_email') },
            { key: 'smsNotifications', label: p('notify_sms') },
            { key: 'wpsPaymentAlerts', label: p('notify_wps') },
            { key: 'gosiDeadlineReminders', label: p('notify_gosi') },
            { key: 'systemAlerts', label: p('notify_system') },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!(prefs.notifications?.[key] ?? DEFAULT_NOTIFICATION_PREFS[key])}
                onChange={(e) => handleNotificationChange(key, e.target.checked)}
                className="rounded"
              />
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>{label}</span>
            </label>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', marginTop: '0.75rem' }}>{p('notify_sms_hint')}</p>
      </motion.div>

      {/* Security — password */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={cardStyle}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
          <Shield size={20} />
          {p('security')}
        </h2>

        {!isChangingPassword ? (
          <div>
            <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.75rem' }}>{p('password_description')}</p>
            <ul style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', marginBottom: '1rem', paddingRight: '1.25rem', listStyle: 'disc' }}>
              <li>{p('password_rule_min_length')}</li>
              <li>{p('password_rule_uppercase')}</li>
              <li>{p('password_rule_lowercase')}</li>
              <li>{p('password_rule_number')}</li>
              <li>{p('password_rule_special')}</li>
            </ul>
            <motion.button
              type="button"
              onClick={() => setIsChangingPassword(true)}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold"
            >
              <Lock size={16} />
              {p('change_password')}
            </motion.button>
          </div>
        ) : (
          <form onSubmit={handlePasswordSubmit}>
            {['currentPassword', 'newPassword', 'confirmPassword'].map((field) => (
              <div key={field} style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}><Lock size={16} />{p(field === 'currentPassword' ? 'current_password' : field === 'newPassword' ? 'new_password' : 'confirm_password')}</label>
                <input
                  type="password"
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                />
              </div>
            ))}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>{p('security_question')}</label>
              <input type="text" name="securityQuestion" value={formData.securityQuestion} onChange={handleChange} placeholder={p('security_question_optional')} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>{p('security_answer')}</label>
              <input type="text" name="securityAnswer" value={formData.securityAnswer} onChange={handleChange} placeholder={p('security_answer_optional')} style={inputStyle} />
            </div>
            <div className="flex gap-3">
              <motion.button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white rounded-xl font-semibold disabled:opacity-60">
                <CheckCircle size={16} />{p('update')}
              </motion.button>
              <motion.button type="button" onClick={() => setIsChangingPassword(false)} className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold">
                {t('common.cancel')}
              </motion.button>
            </div>
          </form>
        )}

        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              name="twoFactorEnabled"
              checked={formData.twoFactorEnabled}
              onChange={handleChange}
              className="rounded"
            />
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>{p('two_factor_auth')}</span>
          </label>

          {loginHistory.length > 0 ? (
            <div>
              <h3 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>{p('login_history')}</h3>
              <ul style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem' }}>
                {loginHistory.slice(0, 5).map((entry, idx) => (
                  <li key={idx} style={{ marginBottom: '0.35rem' }}>
                    {fmtDate(entry.at)} — {entry.device || entry.ip || p('unknown_device')}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </motion.div>

      {/* Logout */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} style={cardStyle}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
          <LogOut size={20} />
          {p('logout')}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>{p('logout_description')}</p>
        <motion.button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl font-semibold"
        >
          <LogOut size={16} />
          {p('logout_button')}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default Profile;
